import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';
import {AudioUpload, Conversion, Transcription} from '../../domain/types';
import {transcriptionsDataSelector} from './transcription.selectors';
import {audioDataSelector} from './audio.selectors';

export const conversionsDataSelector = (state: RootState) => state.conversions.conversions;
export const projectConversionsDataSelector = (state: RootState) => state.conversions.projectConversions;
export const conversionsLoadingSelector = (state: RootState) => state.conversions.loading;
export const conversionsErrorSelector = (state: RootState) => state.conversions.error;
export const conversionsIsConvertingSelector = (state: RootState) => state.conversions.isConverting;
export const conversionsOutputSelector = (state: RootState) => state.conversions.output;
export const conversionsTranscriptionFilesSelector = (state: RootState) => state.conversions.transcriptionFiles;
export const conversionsShowConversionsSelector = (state: RootState) => state.conversions.showConversions;

const byNewest = (a: Conversion, b: Conversion) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

/**
 * Newest first. A conversion list is read as "what did I just make", never alphabetically.
 *
 * The two fields stay separate rather than merging like the workspace does: this page lets the user
 * switch between one project and everything, so which set is on screen is a deliberate choice
 * instead of something to reconstruct.
 */
export const sortedConversionsSelector = createSelector(
  [conversionsDataSelector],
  conversions => [...conversions].sort(byNewest)
);

export const sortedProjectConversionsSelector = createSelector(
  [projectConversionsDataSelector],
  conversions => [...conversions].sort(byNewest)
);

/**
 * The conversion was made before the backend recorded which audio file its XML points at.
 *
 * This is "we don't know", not "it's broken". The backend now refuses to write a conversion it
 * cannot name a clip in, so a recorded `audioPath` is a guarantee and its absence is only the
 * absence of one — most of these XMLs are fine, and the list says so rather than condemning them.
 * The set is fixed and shrinking; nothing here should grow to match it.
 *
 * `== null` is deliberate: it catches the field being absent as well as explicitly null.
 */
export function isMediaReferenceUnrecorded(conversion: Conversion): boolean {
  return conversion.audioPath == null;
}

/**
 * What a conversion would have to be re-run against: the transcription it came out of, the audio
 * that transcription came out of, and the three values `/convertxml` needs that the conversion
 * record itself does not carry.
 */
export interface ConversionSource {
  transcription: Transcription;
  /** Null for a transcription created by the older CSV-import flow — there is no upload behind it. */
  audio: AudioUpload | null;
  /**
   * The media reference a regenerated XML would name its clips with. The local path first, because
   * a full path relinks on its own where a bare filename opens offline; the uploaded filename is
   * the fallback the server-side flow always has.
   */
  mediaReference: string | null;
  sampleRate?: number;
  duration?: number;
}

const firstNonBlank = (...candidates: (string | null | undefined)[]): string | null =>
  candidates.find(candidate => candidate?.trim())?.trim() ?? null;

/**
 * Keyed by the CSV's R2 key, which is the only link a conversion has back to its transcription —
 * `conversion.transcriptionId` is always undefined, the backend does not return it. Same join the
 * workspace does, for the same reason: derive it once rather than in every row.
 */
export const conversionSourcesSelector = createSelector(
  [transcriptionsDataSelector, audioDataSelector],
  (transcriptions, uploads): Map<string, ConversionSource> => {
    const audioById = new Map(uploads.map(upload => [upload.id, upload]));

    return new Map(transcriptions.map((transcription) => {
      const audio = (transcription.audioUploadId && audioById.get(transcription.audioUploadId)) || null;

      return [transcription.csvR2Key, {
        transcription,
        audio,
        mediaReference: firstNonBlank(
          transcription.audioPath,
          transcription.audioFilename,
          audio?.localAudioPath,
          audio?.name
        ),
        // The audio record is the better source: the browser probed both at upload time, while the
        // transcription row only carries a sample rate on the rows that happen to have one.
        sampleRate: audio?.sampleRate ?? transcription.sampleRate,
        duration: audio?.duration ?? transcription.duration,
      }];
    }));
  }
);

/**
 * Why this conversion cannot simply be run again, or `null` when it can.
 *
 * Re-running needs more than the conversion record holds, and the pieces go missing in different
 * ways — so the row either offers the action or says what is in the way, never a button that fails
 * on click. Legacy rows fail these checks often: the same era that did not record a media reference
 * frequently has no sample rate either.
 */
export function describeRegenerateBlock(
  conversion: Conversion,
  source: ConversionSource | undefined
): string | null {
  if (conversion.mode === 'beep') {
    // Beep mode lays a second clip into the timeline and we have nothing to name it with — see
    // `assertMediaIsNamed`. Downgrading it to silence would hand back a different edit.
    return 'This one beeped the censored words, and beep sounds are not available on the web yet.';
  }

  if (!conversion.wordListId) {
    return 'The word list behind this conversion was not recorded, so it cannot be repeated exactly.';
  }

  if (!source) {
    return 'The transcription this came from is no longer here, so there is nothing to convert again.';
  }

  if (source.transcription.status !== 'completed') {
    return 'The transcription this came from is not finished.';
  }

  if (!source.mediaReference) {
    return 'No audio file name was recorded for the transcription behind this, so a new XML would '
      + 'have the same empty media reference. Convert it again from the workspace, where you can '
      + 'type one in.';
  }

  if (source.sampleRate === undefined || source.duration === undefined) {
    return 'The source audio\'s sample rate and duration were never recorded, and the converter '
      + 'needs both.';
  }

  return null;
}
